/**
 * Progressive Loading Configuration
 *
 * This configuration defines the loading strategy for map assets.
 * Assets are loaded in multiple steps with increasing detail levels.
 */

export const PROGRESSIVE_LOADING_LEVELS = [6, 5, 4, 3, 2, 1] as const

export type LoadingLevel = (typeof PROGRESSIVE_LOADING_LEVELS)[number] | null

export interface LoadingStepConfig {
  /**
   * Geohash precision level (null means load all remaining data)
   */
  level: LoadingLevel
  /**
   * Label key for i18n translation
   */
  labelKey: string
  /**
   * Description of what this step loads
   */
  description: string
  /**
   * Whether to await this step before starting the next
   */
  awaitCompletion: boolean
  /**
   * Whether to display results immediately after fetching
   */
  displayImmediately: boolean
}

/**
 * Loading step configurations
 * Step 1: Load viewport at precision 6 (most detailed), await, display
 * Step 2: Load viewport at precision 5, await, then trigger step 3
 * Step 3: Load viewport at precision 4 (async), display step 2 while loading
 * Step 4: Load all remaining data (async), display step 3 while loading
 */
export const LOADING_STEPS: Record<string, LoadingStepConfig> = {
  VIEWPORT_DETAILED: {
    level: 5,
    labelKey: 'map.loading.detailedView',
    description: 'Loading detailed viewport',
    awaitCompletion: true,
    displayImmediately: true
  },
  VIEWPORT_MEDIUM: {
    level: 4,
    labelKey: 'map.loading.mediumDetail',
    description: 'Loading medium detail viewport',
    awaitCompletion: true,
    displayImmediately: false // Display after step 3 starts
  },
  VIEWPORT_OVERVIEW: {
    level: 3,
    labelKey: 'map.loading.overview',
    description: 'Loading overview viewport',
    awaitCompletion: false,
    displayImmediately: false // Display after step 4 starts
  },
  GLOBAL: {
    level: null,
    labelKey: 'map.loading.completeMap',
    description: 'Loading complete map',
    awaitCompletion: false,
    displayImmediately: false // Display as it loads
  }
}

/**
 * Get ordered array of loading steps
 */
export const getLoadingStepsArray = (): LoadingStepConfig[] => {
  return [
    LOADING_STEPS.VIEWPORT_DETAILED,
    LOADING_STEPS.VIEWPORT_MEDIUM,
    LOADING_STEPS.VIEWPORT_OVERVIEW,
    LOADING_STEPS.GLOBAL
  ]
}

/**
 * Total number of loading steps
 */
export const TOTAL_LOADING_STEPS = Object.keys(LOADING_STEPS).length

/**
 * Enable/disable progressive loading (useful for testing)
 */
export const ENABLE_PROGRESSIVE_LOADING = true
